import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// react-resizable-panels v4 — Group / Panel / Separator (renamed from
// PanelGroup / Panel / PanelResizeHandle in v3). Orientation is a prop on Group.
import { Group, Panel, Separator } from 'react-resizable-panels';
import { ChevronLeft, X } from 'lucide-react';
import { cn } from '../lib/cn';

type Direction = 'horizontal' | 'vertical';

type OpenOpts = {
  /** 'horizontal' = side-by-side (primary left, detail right).
   *  'vertical'   = stacked     (primary top,  detail bottom). */
  direction?: Direction;
  /** Initial size of the detail panel, in percent. The primary takes the rest. */
  defaultSize?: number;
  /** Anything renderable — a detail view, a form, a JSON inspector, etc. */
  content: ReactNode;
};

type SplitState = OpenOpts & { open: boolean };

type SplitApi = {
  open: (opts: OpenOpts) => void;
  close: () => void;
  isOpen: boolean;
};

const SplitCtx = createContext<SplitApi | null>(null);

/** True below Tailwind's `md` (768px). The split is a desktop pattern — two
 *  resizable columns on a phone give you two cramped columns — so on narrow
 *  viewports the detail takes over the screen instead, which is also how the
 *  customer app is meant to read: a list, then a full-screen detail with a way
 *  back. */
function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && !window.matchMedia('(min-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setNarrow(!mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

/** Wraps the routed page content and exposes `useSplit()` so any descendant
 *  can pop open a secondary panel. The primary (the routed page) keeps
 *  rendering — `open` only mounts the second pane and a draggable divider
 *  around it. `close` unmounts the second pane; the primary returns to full
 *  area. */
export function SplitProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SplitState>({
    open: false,
    direction: 'horizontal',
    defaultSize: 40,
    content: null,
  });

  const open = useCallback((opts: OpenOpts) => {
    setState({
      open: true,
      direction: opts.direction ?? 'horizontal',
      defaultSize: opts.defaultSize ?? 40,
      content: opts.content,
    });
  }, []);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false, content: null }));
  }, []);

  // Close any open drawer when the route changes — a panel opened on one page
  // shouldn't linger over the next one.
  const { pathname } = useLocation();
  useEffect(() => {
    close();
  }, [pathname, close]);

  const api = useMemo<SplitApi>(() => ({ open, close, isOpen: state.open }), [
    open,
    close,
    state.open,
  ]);

  return (
    <SplitCtx.Provider value={api}>
      <SplitShell state={state} onClose={close}>
        {children}
      </SplitShell>
    </SplitCtx.Provider>
  );
}

export function useSplit(): SplitApi {
  const ctx = useContext(SplitCtx);
  if (!ctx) throw new Error('useSplit must be used inside <SplitProvider>');
  return ctx;
}

function SplitShell({
  state,
  onClose,
  children,
}: {
  state: SplitState;
  onClose: () => void;
  children: ReactNode;
}) {
  const narrow = useNarrowViewport();
  const { t } = useTranslation();
  const backLabel = t('action.back');

  if (!state.open) {
    // Single pane — the routed page fills the entire main area.
    return <div className="h-full w-full overflow-auto">{children}</div>;
  }

  // Narrow viewport: the detail replaces the list rather than sharing the width
  // with it. The list stays mounted-in-spirit (it re-renders on close) but is not
  // painted, so there is no two-column squeeze on a phone.
  if (narrow) {
    return (
      <div className="h-full w-full flex flex-col bg-card">
        <div className="h-10 px-3 flex items-center border-b border-border bg-card shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded px-1.5 py-1 text-sm text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <ChevronLeft className="h-4 w-4 shrink-0" />
            {backLabel}
          </button>
        </div>
        <div className="flex-1 overflow-auto">{state.content}</div>
      </div>
    );
  }

  const isHorizontal = state.direction === 'horizontal';
  const primarySize = 100 - (state.defaultSize ?? 40);

  return (
    <Group
      orientation={isHorizontal ? 'horizontal' : 'vertical'}
      className="h-full w-full"
    >
      {/* Primary — the routed page. Stays first-mounted; only the detail
       *  panel mounts/unmounts when split toggles. */}
      <Panel defaultSize={primarySize} minSize={20} className="overflow-auto">
        {children}
      </Panel>

      <Separator
        className={cn(
          'group relative flex items-center justify-center bg-transparent',
          isHorizontal ? 'w-3 cursor-col-resize' : 'h-3 cursor-row-resize',
        )}
      >
        {/* Hairline along the full length of the divider. */}
        <span
          className={cn(
            'absolute bg-border group-hover:bg-primary/60 transition-colors',
            isHorizontal
              ? 'top-0 bottom-0 left-1/2 -translate-x-1/2 w-px'
              : 'left-0 right-0 top-1/2 -translate-y-1/2 h-px',
          )}
        />
        {/* Centered thick grip — visual affordance for dragging. */}
        <span
          className={cn(
            'relative rounded-full bg-border group-hover:bg-primary transition-colors',
            isHorizontal ? 'h-8 w-1' : 'w-8 h-1',
          )}
        />
      </Separator>

      <Panel defaultSize={state.defaultSize} minSize={15} className="overflow-auto">
        <div
          className={cn(
            'h-full w-full flex flex-col bg-card',
            isHorizontal
              ? 'border-l border-border shadow-[-8px_0_30px_rgba(21,23,28,0.06)]'
              : 'border-t border-border shadow-[0_-8px_30px_rgba(21,23,28,0.06)]',
          )}
        >
          <div className="h-10 px-3 flex items-center justify-end border-b border-border bg-card shrink-0">
            <button
              type="button"
              aria-label="Close panel"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-auto">{state.content}</div>
        </div>
      </Panel>
    </Group>
  );
}
